import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { ReviewStars } from "./ReviewStars";

interface ProductCardReviewsProps {
  variantId: string;
  productHandle?: string;
}

type Aggregate = { avg: number; count: number };

let variantCache: Map<string, Aggregate> | null = null;
let handleCache: Map<string, Aggregate> | null = null;
let inflight: Promise<void> | null = null;
const listeners = new Set<() => void>();

const loadAll = (): Promise<void> => {
  if (variantCache && handleCache) return Promise.resolve();
  if (inflight) return inflight;
  inflight = (async () => {
    const { data } = await supabase
      .from("reviews_public")
      .select("variant_id,product_handle,rating")
      .eq("approved", true);
    const byVariant = new Map<string, { sum: number; count: number }>();
    const byHandle = new Map<string, { sum: number; count: number }>();
    (data ?? []).forEach((r: any) => {
      if (r.variant_id) {
        const cur = byVariant.get(r.variant_id) ?? { sum: 0, count: 0 };
        cur.sum += r.rating;
        cur.count += 1;
        byVariant.set(r.variant_id, cur);
      }
      if (r.product_handle) {
        const cur = byHandle.get(r.product_handle) ?? { sum: 0, count: 0 };
        cur.sum += r.rating;
        cur.count += 1;
        byHandle.set(r.product_handle, cur);
      }
    });
    const vResult = new Map<string, Aggregate>();
    byVariant.forEach((v, k) => vResult.set(k, { avg: v.sum / v.count, count: v.count }));
    const hResult = new Map<string, Aggregate>();
    byHandle.forEach((v, k) => hResult.set(k, { avg: v.sum / v.count, count: v.count }));
    variantCache = vResult;
    handleCache = hResult;
    inflight = null;
    listeners.forEach((l) => l());
  })();
  return inflight;
};

const resolve = (variantId: string, productHandle?: string): Aggregate | null => {
  const v = variantCache?.get(variantId);
  if (v && v.count > 0) return v;
  if (productHandle) return handleCache?.get(productHandle) ?? null;
  return null;
};

export const ProductCardReviews = ({ variantId, productHandle }: ProductCardReviewsProps) => {
  const [agg, setAgg] = useState<Aggregate | null>(() => resolve(variantId, productHandle));

  useEffect(() => {
    let active = true;
    const update = () => {
      if (!active) return;
      setAgg(resolve(variantId, productHandle) ?? { avg: 0, count: 0 });
    };
    if (variantCache && handleCache) update();
    else {
      listeners.add(update);
      loadAll().then(update);
    }
    return () => {
      active = false;
      listeners.delete(update);
    };
  }, [variantId, productHandle]);

  if (!agg || agg.count === 0) return null;

  return (
    <div className="flex items-center gap-1.5">
      <ReviewStars rating={Math.round(agg.avg)} size={12} />
      <span className="text-xs text-muted-foreground">({agg.count})</span>
    </div>
  );
};
